import { defaultAlchemysts, sampleEmailInboxCredentials } from '@/constants';
import { AlchemystStoreType, SnackBarEntry } from '@/types/appStates';
import { applyDiff } from '@/utils/object-diff';
import { create } from 'zustand';

const useAlchemystStoreForAi = create<AlchemystStoreType>((set, get) => ({
  // Initial state setup
  alchemyst: defaultAlchemysts[0],
  isCommandBarOpen: false,
  isNavBarDrawerPresent: false,
  drawerComponents: [],
  chatChannel: 'platform.converse',
  previousChannel: '',
  pinnedCount: 0,
  chatHistory: [],
  // Chat recordings for rrweb
  chatRecordings: {},
  theme: 'dark',
  settings: {
    isAutopilot: false,
  },
  // Document viewer state
  documentViewer: {
    fileUrls: [],
    isOpen: false,
    scratchpadContent: undefined,
    activeTabIndex: undefined,
  },
  // Trace visualizer state
  traceVisualizer: {
    isOpen: false,
    traceId: null,
  },
  leadsMapSettings: {},
  // New structure for multiple lead sets
  leadSets: {},
  activeLeadSetId: null,
  leads: [],
  fields: [],
  localLeads: [],
  localFields: [],
  prospectorQuery: {
    searchParams: {
      page: 1,
      per_page: 100,
      annual_revenue: [],
      employee_count: [],
      industries: [],
      titles: [],
      countries: [],
      name: '',
      technologies: [],
      company_domains: [],
      total_funding: [],
      keywords: [],
      states: [],
      cities: [],
    },
  },
  bigQuery: {
    searchParams: {
      page: 1,
      per_page: 100,
      countries: [],
      region: [],
      job_title: [],
      industry: [],
      company_name: [],
      company_size: [],
      skills: [],
      company_founded: [],
    },
  },
  selectedLeads: [],
  inbox: {
    inboxEmails: [],
    threadIds: [],
    emailsToCheck: [],
    autoFetchCampaignEmails: false,
    selectedInboxEmails: [],
  },
  chat: {
    currentChatId: undefined,
    replaySessionID: '',
    chatChannel: 'platform.converse',
    title: '',
    messages: [],
    currentMessage: '',
    thinking: false,
    thinkingProcess: [],
    lastThinkingProcess: [],
    newMessageIds: new Set(),
    loadingHistory: false,
    selectedTuners: [],
    isGeneratingResponse: false,
    tools: {
      deepResearch: false,
      researchIcps: false,
      webSearch: false,
      organizationSearch: false,
    },
  },
  snackbarEntries: [],
  emailInboxCredentials: sampleEmailInboxCredentials,
  currentScrapingSession: null,
  alchemystCopilotAction: null,
  currentFieldNames: [],
  currentAugmentorOutput: null,
  emailBodiesByThreadByEmail: {},
  inboxAuthenticated: false,
  campaignWorkflows: [],
  totalFiles: 0,
  uploadedFiles: 0,

  // Generic setState method
  setStoreState: (newState: Partial<AlchemystStoreType>) => {
    set((state) => ({ ...state, ...newState }));
  },

  // Add new methods for chat history with diff tracking
  updateChatWithDiff: (chatId: string, chatDiff: Record<string, any>) => {
    set((state) => {
      const chatRecordings = { ...state.chatRecordings };
      const existingChat = chatRecordings[chatId] || {
        snapshots: [],
        currentIndex: -1,
      };

      // Create new snapshot with the diff
      const newSnapshot = {
        timestamp: new Date().toISOString(),
        diff: chatDiff,
        fullState:
          existingChat.currentIndex >= 0
            ? applyDiff(
                existingChat.snapshots[existingChat.currentIndex].fullState,
                chatDiff
              )
            : chatDiff,
      };

      // Add new snapshot and update current index
      const newSnapshots = [...existingChat.snapshots, newSnapshot];
      const newIndex = newSnapshots.length - 1;

      chatRecordings[chatId] = {
        snapshots: newSnapshots,
        currentIndex: newIndex,
      };

      return { chatRecordings };
    });
  },

  // Get the current state of a chat
  getChatState: (chatId: string) => {
    const state = get();
    const chatRecording = state.chatRecordings[chatId];
    if (!chatRecording || chatRecording.currentIndex < 0) {
      return null;
    }
    return chatRecording.snapshots[chatRecording.currentIndex].fullState;
  },

  // Add a state snapshot to a chat recording
  addSnapshot: (
    chatId: string,
    snapshot: {
      timestamp: number;
      fullState: Record<string, any>;
      diff: Record<string, any>;
    }
  ) => {
    set((state) => {
      const chatRecordings = { ...state.chatRecordings };
      const existingChat = chatRecordings[chatId] || {
        snapshots: [],
        currentIndex: -1,
      };

      // Add the new snapshot with converted timestamp
      const formattedSnapshot = {
        ...snapshot,
        timestamp: new Date(snapshot.timestamp).toISOString(), // Convert to string format
      };

      const newSnapshots = [...existingChat.snapshots, formattedSnapshot];
      const newIndex = newSnapshots.length - 1;

      chatRecordings[chatId] = {
        ...existingChat,
        snapshots: newSnapshots,
        currentIndex: newIndex,
      };

      console.log(`Added snapshot ${newIndex + 1} to chat ${chatId}`);

      return { chatRecordings };
    });
  },

  // Add rrweb recording for a specific chat
  addRrwebRecording: (chatId: string, recording: any) => {
    set((state) => {
      const chatRecordings = { ...state.chatRecordings };
      const existingChat = chatRecordings[chatId] || {
        snapshots: [],
        currentIndex: -1,
        rrwebEvents: [],
      };

      // Process the new recording
      let newEvents = [];

      // Check if recording is an array or a single event
      if (Array.isArray(recording)) {
        newEvents = recording;
      } else {
        newEvents = [recording];
      }

      // Ensure we have valid events (must have timestamp and type)
      newEvents = newEvents.filter(
        (event) =>
          event &&
          typeof event === 'object' &&
          'timestamp' in event &&
          'type' in event
      );

      if (newEvents.length === 0) {
        console.warn('No valid events to add to recording');
        return { chatRecordings }; // No change
      }

      console.log(
        `Adding ${newEvents.length} events to chat ${chatId} recording`
      );

      // Add to existing events
      const updatedChat = {
        ...existingChat,
        rrwebEvents: [...(existingChat.rrwebEvents || []), ...newEvents],
      };

      chatRecordings[chatId] = updatedChat;

      // Log for debugging
      console.log(
        `Chat ${chatId} now has ${updatedChat.rrwebEvents.length} events`
      );

      return { chatRecordings };
    });
  },
}));

const updateSnackbarEntry = (entryUpdated: Partial<SnackBarEntry>) => {
  const snackbarEntries = useAlchemystStoreForAi.getState().snackbarEntries;
  const setSnackbarEntries = useAlchemystStoreForAi.getState().setStoreState;
  const entryToBeModified = snackbarEntries.filter(
    (entry) => entry.notificationId !== entryUpdated.notificationId
  );

  if (entryToBeModified.length === 0) {
    console.log('No entry to be modified.');
    return;
  }

  const filteredSnackbarEntries = snackbarEntries.filter(
    (entry) => entry.notificationId !== entryUpdated.notificationId
  );
  setSnackbarEntries({ snackbarEntries: filteredSnackbarEntries });
  // const modifiedSnackbarEntries = snackbarEntries.map(snackbarEntry => {
  //   if (
  //     snackbarEntry.notificationId === entryUpdated.notificationId &&
  //     !!entryUpdated.notificationId
  //   ) {
  //     return entryUpdated;
  //   } else {
  //     return snackbarEntry;
  //   }
  // });
  const modifiedSnackbarEntries = [
    ...filteredSnackbarEntries,
    { ...entryToBeModified[0], ...entryUpdated },
  ];

  setSnackbarEntries({ snackbarEntries: modifiedSnackbarEntries });
};

export { updateSnackbarEntry };

export default useAlchemystStoreForAi;
